import { UTCDate } from '@date-fns/utc';
import { endOfMonth, endOfQuarter, endOfYear, format, startOfMonth, startOfQuarter, startOfYear, subQuarters, subYears, subMonths } from 'date-fns';
import { omit } from 'lodash';

import { API } from '../api';
import { IFetchPageQuery } from '../IFetchQueryOptions';
import { IPagination } from '../IPagination';
import { IVendorBillItem } from './interface';
import { DATE_FORMAT_CSV } from '../commonConst';
import { exportCSVData } from '../common/exportCSVData';
import { parseClearPeriodValue, parseDateRangeValue } from '../../pages/settlements/view/vendorBillItems/sidebar/utils';

export const fetchVendorBillItems = async (
    { sorter, include, filter, search, company_id = null }: IFetchPageQuery,
    signal?: AbortSignal | null
): Promise<IPagination<IVendorBillItem>> => {
    const path = `/vendor-bill-items?${new URLSearchParams({
        ...prepareOrder(sorter),
        ...prepareFilter(filter),
        ...(search && { search }),
        ...(company_id && { companyId: company_id }),
        ...(include && { include }),
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || `Cannot load list of vendor bill items!`);
};

function prepareOrder(sorter: IFetchPageQuery['sorter']) {
    if (!sorter || !sorter.field || !sorter.order) {
        return {};
    }

    return { sortField: sorter.field, sortOrder: sorter.order };
}

function prepareFilter(vendorBillItemFilter: IFetchPageQuery['filter']): IFetchPageQuery['filter'] {
    const { periodStart, periodEnd } = getDateRangeFromPeriod(vendorBillItemFilter?.period);

    return {
        ...omit(vendorBillItemFilter, 'period'),
        ...(periodStart && { periodStart: periodStart.toISOString() }),
        ...(periodEnd && { periodEnd: periodEnd.toISOString() }),
    };
}

function getDateRangeFromPeriod(period?: string): { periodStart: Date | null; periodEnd: Date | null } {
    const today = new UTCDate();

    const clearPeriod = parseClearPeriodValue(period);

    switch (clearPeriod) {
        case 'current_month':
            return {
                periodStart: startOfMonth(today),
                periodEnd: endOfMonth(today),
            };

        case 'previous_month':
            const priorMonthDate = subMonths(today, 1);
            return {
                periodStart: startOfMonth(priorMonthDate),
                periodEnd: endOfMonth(priorMonthDate),
            };

        case 'current_quarter':
            return {
                periodStart: startOfQuarter(today),
                periodEnd: endOfQuarter(today),
            };

        case 'previous_quarter': {
            const previousQuarterDate = subQuarters(today, 1);
            return {
                periodStart: startOfQuarter(previousQuarterDate),
                periodEnd: endOfQuarter(previousQuarterDate),
            };
        }

        case 'year_to_date':
            return {
                periodStart: startOfYear(today),
                periodEnd: today,
            };

        case 'previous_year': {
            const previousYearDate = subYears(today, 1);
            return {
                periodStart: startOfYear(previousYearDate),
                periodEnd: endOfYear(previousYearDate),
            };
        }
        case 'custom_range':
            const dateRange = parseDateRangeValue(period);

            if (!dateRange)
                return {
                    periodStart: null,
                    periodEnd: null,
                };

            return {
                periodStart: dateRange.start,
                periodEnd: dateRange.end,
            };

        case 'all':
        default:
            return {
                periodStart: null,
                periodEnd: null,
            };
    }
}

export const exportVendorBillItems = async ({ include, filter, search, company_id = null }: IFetchPageQuery) => {
    const query = new URLSearchParams({
        format: 'csv',
        ...prepareFilter(filter),
        ...(search && { search }),
        ...(company_id && { companyId: company_id }),
        ...(include && { include }),
    });

    const response = await API.fetch(`/vendor-bill-items?${query}`, {
        headers: {
            'Content-Type': 'text/csv',
        },
    });

    const respBody: string = await response.text();

    if (response.ok) {
        const fileName = `settlement-data--${format(new Date(), DATE_FORMAT_CSV)}.csv`;
        exportCSVData(respBody, fileName);
    } else {
        const respBody: any = await response.json();
        throw new Error(respBody?.error?.message || `Cannot export data!`);
    }
};
